/**
 * Root Application Router
 * Delegates to domain-specific route modules
 */
import { Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import DefaultRedirect from '@/domains/internal/auth/components/DefaultRedirect';
import ProtectedRoute from '@/domains/internal/auth/components/ProtectedRoute';
import TenantOnlyRoute from '@/domains/internal/auth/components/TenantOnlyRoute';
import AdminOnlyRoute from '@/domains/admin/auth/components/AdminOnlyRoute';
import Layout from '@/shared/components/layout/Layout';

// Eager load critical auth pages
import Login from '@/domains/internal/auth/pages/Login';
import ForgotPassword from '@/domains/internal/auth/pages/ForgotPassword';
import ResetPassword from '@/domains/internal/auth/pages/ResetPassword';
import ChangePassword from '@/domains/internal/auth/pages/ChangePassword';

// Admin Portal auth pages
import AdminLogin from '@/domains/admin/auth/pages/AdminLogin';

// Eager load dashboard
import Dashboard from '@/domains/internal/dashboard/pages/Dashboard';

// Lazy load other pages
const PatientRegistration = lazy(() => import('@/domains/internal/patients/pages/PatientRegistration'));
const VisitsList = lazy(() => import('@/domains/internal/visits/pages/VisitsList'));

// Admin pages
const TenantManagement = lazy(() => import('@/domains/admin/tenant-management/TenantManagementPage'));
const Queue = lazy(() => import('@/domains/internal/queue/pages/Queue'));
const DoctorPortal = lazy(() => import('@/domains/internal/queue/pages/DoctorPortal'));
const Billing = lazy(() => import('@/domains/internal/payments/pages/Billing'));
const InvoiceView = lazy(() => import('@/domains/internal/payments/pages/InvoiceView'));
const StaffManagement = lazy(() => import('@/domains/internal/staff/pages/StaffManagement'));
const TimeTracking = lazy(() => import('@/domains/internal/staff/pages/TimeTracking'));
const MyTimeOff = lazy(() => import('@/domains/internal/staff/pages/MyTimeOff'));
const TimeOffApprovals = lazy(() => import('@/domains/internal/staff/pages/TimeOffApprovals'));
const DepartmentManagement = lazy(() => import('@/domains/internal/facilities/pages/DepartmentManagement'));
const TestManagement = lazy(() => import('@/domains/internal/lab-tests/pages/TestManagement'));
const ReferralManagement = lazy(() => import('@/domains/internal/referrals/pages/ReferralManagement'));
const Reports = lazy(() => import('@/domains/internal/reports/pages/Reports'));
const UserProfile = lazy(() => import('@/domains/internal/user-profile/pages/UserProfile'));

// Referral Portal Pages
const ReferralPortalLogin = lazy(() => import('@/domains/external/referral-portal/auth/pages/ReferralPortalLogin'));
const ReferralChangePassword = lazy(() => import('@/domains/external/referral-portal/auth/pages/ReferralChangePassword'));
const CommissionDashboard = lazy(() => import('@/domains/external/referral-portal/dashboard/pages/CommissionDashboard'));
const TeamManagement = lazy(() => import('@/domains/external/referral-portal/team/pages/TeamManagement'));
const LabOrders = lazy(() => import('@/domains/external/referral-portal/lab-orders/pages/LabOrders'));
const LabOrderSuccess = lazy(() => import('@/domains/external/referral-portal/lab-orders/pages/LabOrderSuccess'));

// Public Pages (no auth required)
const MobileUpload = lazy(() => import('@/domains/public/upload/pages/MobileUpload'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center p-8">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--color-primary)]"></div>
  </div>
);

export const AppRoutes = () => {
  return (
    <Routes>
      {/* Tenant Auth Routes - Only accessible on tenant subdomains */}
      <Route path="/login" element={
        <TenantOnlyRoute>
          <Login />
        </TenantOnlyRoute>
      } />
      <Route path="/forgot-password" element={
        <TenantOnlyRoute>
          <ForgotPassword />
        </TenantOnlyRoute>
      } />
      <Route path="/reset-password" element={
        <TenantOnlyRoute>
          <ResetPassword />
        </TenantOnlyRoute>
      } />

      {/* Admin Portal Auth Routes - Only accessible on public domain */}
      <Route path="/admin/login" element={
        <AdminOnlyRoute>
          <AdminLogin />
        </AdminOnlyRoute>
      } />

      {/* Protected Password Change */}
      <Route path="/change-password" element={
        <ProtectedRoute>
          <ChangePassword />
        </ProtectedRoute>
      } />

      {/* Root redirect - handles domain-based routing */}
      <Route path="/" element={<DefaultRedirect />} />

      {/* Dashboard */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Layout>
            <Dashboard />
          </Layout>
        </ProtectedRoute>
      } />

      {/* Patients Domain */}
      <Route path="/patients" element={
        <ProtectedRoute requiredRoles={['receptionist', 'admin', 'super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <PatientRegistration />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Visits Domain */}
      <Route path="/visits" element={
        <ProtectedRoute requiredRoles={['receptionist', 'admin', 'super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <VisitsList />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Queue Domain */}
      <Route path="/queue" element={
        <ProtectedRoute requiredRoles={['doctor', 'technician', 'admin', 'super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Queue />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/doctor-portal" element={
        <ProtectedRoute requiredRoles={['doctor', 'technician', 'admin', 'super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <DoctorPortal />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Payments Domain */}
      <Route path="/payments" element={
        <ProtectedRoute requiredRoles={['receptionist', 'admin', 'super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Billing />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/invoices/:invoiceId" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <InvoiceView />
          </Suspense>
        </ProtectedRoute>
      } />

      {/* Staff Domain */}
      <Route path="/staff" element={
        <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <StaffManagement />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/time-tracking" element={
        <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <TimeTracking />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/my-time-off" element={
        <ProtectedRoute>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <MyTimeOff />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      <Route path="/time-off-approvals" element={
        <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <TimeOffApprovals />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Facilities Domain */}
      <Route path="/departments" element={
        <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <DepartmentManagement />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Lab Tests Domain */}
      <Route path="/tests" element={
        <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <TestManagement />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Referrals Domain */}
      <Route path="/referral-management" element={
        <ProtectedRoute requiredRoles={['super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <ReferralManagement />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Admin Portal - Tenant Management */}
      <Route path="/admin/tenants" element={
        <AdminOnlyRoute>
          <Suspense fallback={<PageLoader />}>
            <TenantManagement />
          </Suspense>
        </AdminOnlyRoute>
      } />

      {/* Reports Domain */}
      <Route path="/reports" element={
        <ProtectedRoute requiredRoles={['admin', 'super_admin']}>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <Reports />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      {/* User Profile Domain */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <Layout>
            <Suspense fallback={<PageLoader />}>
              <UserProfile />
            </Suspense>
          </Layout>
        </ProtectedRoute>
      } />

      {/* Referral Portal - Public Routes */}
      <Route path="/referral-portal/login" element={
        <Suspense fallback={<PageLoader />}>
          <ReferralPortalLogin />
        </Suspense>
      } />

      {/* Referral Portal - Protected Routes */}
      <Route path="/referral-portal/change-password" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <ReferralChangePassword />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/referral-portal/dashboard" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <CommissionDashboard />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/referral-portal/team" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <TeamManagement />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/referral-portal/lab-orders" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <LabOrders />
          </Suspense>
        </ProtectedRoute>
      } />

      <Route path="/referral-portal/lab-order-success" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}>
            <LabOrderSuccess />
          </Suspense>
        </ProtectedRoute>
      } />

      {/* Default route for /referral-portal */}
      <Route path="/referral-portal" element={<DefaultRedirect />} />

      {/* Public Upload Page - No authentication required */}
      {/* Accessed by scanning QR code from desktop */}
      <Route path="/upload/:token" element={
        <Suspense fallback={<PageLoader />}>
          <MobileUpload />
        </Suspense>
      } />

      {/* Catch all route */}
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
  );
};
